import { cookieDomainAttr, deleteCookie, readCookie } from './browser-cookie';
import { buildVisitorExternalId } from './event-id';

/**
 * Identifiant de visiteur première partie, cross-subdomain (`.propelsia.ai`) : la vitrine
 * le pose, la page register de l'app le relit. Il sert d'`external_id` aux régies sur les
 * events navigateur d'avant l'inscription (`PageView`, clic CTA), puis suit l'inscrit dans
 * son snapshot d'attribution : Meta relie ainsi le parcours anonyme au compte créé.
 *
 * Traceur marketing (déclaré dans la politique cookies, catégorie « Marketing ») : ne JAMAIS
 * appeler `ensureVisitorId` sans consentement marketing. Valeur aléatoire (UUID v4), aucune
 * donnée dérivée du terminal ni de la personne.
 */
export const VISITOR_ID_COOKIE = 'propelsia_vid';

/** Longueur d'un UUID — borne des schémas qui transportent l'identifiant. */
export const VISITOR_ID_MAX_LENGTH = 36;

const VISITOR_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/**
 * Format accepté (UUID v4 minuscule). Vérifié à la lecture du cookie ET par le backend à la
 * réception : les schémas ne portent volontairement pas de `pattern` (une valeur forgée ne
 * doit jamais faire échouer une inscription en 400 — elle est simplement ignorée).
 */
export function isValidVisitorId(value: unknown): value is string {
  return typeof value === 'string' && VISITOR_ID_RE.test(value);
}

const THIRTEEN_MONTHS_SECONDS = 13 * 30 * 24 * 60 * 60;

/** Identifiant déjà posé (valeur hors format ignorée), sans jamais en créer. */
export function readVisitorId(): string | undefined {
  const value = readCookie(VISITOR_ID_COOKIE);
  return isValidVisitorId(value) ? value : undefined;
}

/**
 * Identifiant du visiteur, créé au premier appel. ⚠️ Sous consentement marketing
 * uniquement (cf. en-tête). `undefined` hors navigateur ou sans `crypto.randomUUID`
 * (contexte non sécurisé) : pas d'identifiant plutôt qu'un identifiant faible.
 */
export function ensureVisitorId(): string | undefined {
  const existing = readVisitorId();
  if (existing || typeof document === 'undefined') return existing;
  const cryptoObj = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (!cryptoObj || typeof cryptoObj.randomUUID !== 'function') return undefined;
  const created = cryptoObj.randomUUID().toLowerCase();
  document.cookie = `${VISITOR_ID_COOKIE}=${created}; path=/; max-age=${THIRTEEN_MONTHS_SECONDS}; SameSite=Lax${cookieDomainAttr()}`;
  // Cookie refusé (bloqué, domaine rejeté) : sans relecture, chaque appel inventerait un
  // nouvel id et pixel / CAPI ne désigneraient jamais la même personne.
  return readVisitorId() === created ? created : undefined;
}

/** Retrait du consentement marketing : le traceur ne survit pas au refus. */
export function clearVisitorId(): void {
  deleteCookie(VISITOR_ID_COOKIE);
}

/** SHA-256 hexadécimal via WebCrypto (navigateur) ; `undefined` si l'API manque. */
export async function sha256HexBrowser(value: string): Promise<string | undefined> {
  const subtle = typeof globalThis !== 'undefined' ? globalThis.crypto?.subtle : undefined;
  if (!subtle) return undefined;
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest))
    .map((b): string => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * `external_id` hashé du visiteur pour l'advanced matching du Pixel Meta (`fbq('init', id,
 * { external_id })`). Hashé ICI, comme le `pro_<id>` de la page register : le serveur hashe
 * la même chaîne (`buildVisitorExternalId`), les deux remontées désignent la même personne.
 */
export async function hashedVisitorExternalId(visitorId: string): Promise<string | undefined> {
  return sha256HexBrowser(buildVisitorExternalId(visitorId));
}

/**
 * `external_id` hashé du visiteur à passer au `fbq('init')`, en créant l'identifiant au
 * besoin. ⚠️ Sous consentement marketing uniquement. Partagé par la vitrine et l'app pour
 * qu'elles hashent le même id de la même façon ; `undefined` sur toute erreur (le pixel se
 * charge alors sans external_id plutôt que pas du tout).
 */
export async function resolveMetaVisitorExternalId(): Promise<string | undefined> {
  const visitorId = ensureVisitorId();
  if (!visitorId) return undefined;
  try {
    return await hashedVisitorExternalId(visitorId);
  } catch {
    return undefined;
  }
}
